import { Modal } from '@/components/admin/Modal';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import type { ResponsePromptVersion } from '@innermate/shared';
import { diffLines } from 'diff';
import { Fragment, useMemo } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 비교 대상 두 버전. left=현재 활성, right=사용자가 고른 비교 버전. */
  left: { label: string; version: ResponsePromptVersion } | null;
  right: { label: string; version: ResponsePromptVersion } | null;
}

interface LineRow {
  /** 좌측 라인 — undefined 면 우측에만 존재 (added). */
  leftText?: string;
  /** 우측 라인 — undefined 면 좌측에만 존재 (removed). */
  rightText?: string;
  kind: 'equal' | 'added' | 'removed';
}

/**
 * diff 결과를 좌우 align 된 행 목록으로 변환. unchanged 는 양쪽 같이, added 는 우측에만, removed 는 좌측에만.
 */
function buildSideBySide(leftContent: string, rightContent: string): LineRow[] {
  const parts = diffLines(leftContent, rightContent);
  const rows: LineRow[] = [];
  for (const p of parts) {
    const lines = p.value.split('\n');
    // diffLines 가 마지막 '\n' 뒤 빈 줄을 만들어내는 경우 제거.
    if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
    if (p.added) {
      for (const line of lines) rows.push({ rightText: line, kind: 'added' });
    } else if (p.removed) {
      for (const line of lines) rows.push({ leftText: line, kind: 'removed' });
    } else {
      for (const line of lines) rows.push({ leftText: line, rightText: line, kind: 'equal' });
    }
  }
  return rows;
}

export function VersionDiffModal({ open, onClose, left, right }: Props) {
  const rows = useMemo(() => {
    if (!left || !right) return [];
    return buildSideBySide(left.version.content, right.version.content);
  }, [left, right]);

  if (!left || !right) return null;

  const added = rows.filter((r) => r.kind === 'added').length;
  const removed = rows.filter((r) => r.kind === 'removed').length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="버전 비교"
      description={`${left.label} ↔ ${right.label}`}
      size="xxl"
      layer="high"
      titleActions={
        <div className="flex items-center gap-2 text-xs">
          <Badge
            variant="muted"
            className="bg-emerald-500/15 text-emerald-600 border-emerald-500/40"
          >
            + {added}
          </Badge>
          <Badge
            variant="muted"
            className="bg-destructive/15 text-destructive border-destructive/40"
          >
            − {removed}
          </Badge>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-px bg-border rounded-md overflow-hidden text-xs font-mono">
        <div className="bg-surface-muted/40 px-3 py-1.5 font-semibold text-foreground-muted">
          {left.label}
        </div>
        <div className="bg-surface-muted/40 px-3 py-1.5 font-semibold text-foreground-muted">
          {right.label}
        </div>
        {/* 본문 — 각 행마다 좌/우 cell. 빈 cell 은 공백. */}
        {rows.map((r, i) => {
          // diff 결과는 정렬·결정적이라 인덱스 기반 키 안정적.
          const rowKey = `${i}:${r.kind}`;
          return (
            <Fragment key={rowKey}>
              <div className={cn('px-3 py-0.5 whitespace-pre-wrap leading-relaxed', leftClass(r))}>
                {r.leftText ?? ' '}
              </div>
              <div className={cn('px-3 py-0.5 whitespace-pre-wrap leading-relaxed', rightClass(r))}>
                {r.rightText ?? ' '}
              </div>
            </Fragment>
          );
        })}
      </div>
    </Modal>
  );
}

function leftClass(r: LineRow): string {
  if (r.kind === 'removed') return 'bg-destructive/10 text-destructive';
  if (r.kind === 'added') return 'bg-surface/30 text-foreground-muted/40';
  return 'bg-surface';
}
function rightClass(r: LineRow): string {
  if (r.kind === 'added') return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
  if (r.kind === 'removed') return 'bg-surface/30 text-foreground-muted/40';
  return 'bg-surface';
}
